'use client';

import { ReactNode, createContext, useContext, useEffect, useState } from 'react';
import { AuthUser, getMe } from '@/lib/api/auth';
import { ApiClientError } from '@/lib/api/client';
import { setCurrentUserPermissions } from '@/lib/auth/currentPermissions';
import { setCurrentUserEmail } from '@/lib/auth/currentUser';
import { clearRedirectAttempted } from '@/lib/auth/redirectToLogin';

interface AuthState {
  isLoading: boolean;
  isAuthenticated: boolean;
  user: AuthUser | null;
  error: string | null;
  // True when /me indicates the caller is authenticated globally but has no
  // access to this workspace. Drives the "Access denied" screen in RootLayout
  // so the user isn't told they're "Not signed in". Triggered by either:
  //   - HTTP 403 (legacy contract, pre PR #934)
  //   - HTTP 401 with the workspace-denial message (current contract — the
  //     middleware switched to 401 to force re-auth on blocked/deleted users
  //     but the same path also fires for "user not in workspace User CR").
  // TODO(PLT-8720 follow-up): once compute-api adds a stable `code` field
  // (e.g. WORKSPACE_ACCESS_DENIED) to the response, key off that instead of
  // the human message string.
  accessDenied: boolean;
}

interface AuthContextValue extends AuthState {
  refetch: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const WORKSPACE_ACCESS_DENIAL = /access to this workspace/i;

function isWorkspaceAccessDenied(err: unknown): boolean {
  if (!(err instanceof ApiClientError)) return false;
  if (err.isForbidden) return true;
  return err.isUnauthorized && WORKSPACE_ACCESS_DENIAL.test(err.message);
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>({
    isLoading: true,
    isAuthenticated: false,
    user: null,
    error: null,
    accessDenied: false,
  });

  const fetchUser = async () => {
    setState((prev: AuthState) => ({
      ...prev,
      isLoading: true,
      error: null,
      accessDenied: false,
    }));

    try {
      // Reuse the /me probe kicked off from index.html on first boot so the
      // request runs in parallel with JS download instead of after React mount.
      // The inline script clears itself after consumption to avoid stale reuse
      // on subsequent refetch() calls.
      type MeWindow = Window & { __e6MePromise?: Promise<unknown> | null };
      const w = window as MeWindow;
      const inlinePromise = w.__e6MePromise;
      w.__e6MePromise = null;
      const response = inlinePromise
        ? (((await inlinePromise) as Awaited<ReturnType<typeof getMe>> | null) ?? (await getMe()))
        : await getMe();
      const user = response.user ?? null;
      if (response.authenticated) {
        // A session now exists in this tab — release redirectToLogin's
        // one-shot loop guard so a later, unrelated sign-out here still gets
        // the deep-linked handoff instead of the bare-URL fallback for the
        // rest of the tab's life. See clearRedirectAttempted for why this is
        // the hook point rather than HandoffCompletePage's 'ok' report.
        clearRedirectAttempted();
      }
      setCurrentUserEmail(user?.email ?? null);
      setCurrentUserPermissions(
        user
          ? {
              global: user.permissions?.global,
              isAdmin:
                user.permissions?.isAdmin === true || user.permissions?.isSuperAdmin === true,
            }
          : null
      );
      setState({
        isLoading: false,
        isAuthenticated: response.authenticated,
        user,
        error: null,
        accessDenied: false,
      });
    } catch (err) {
      // On error (e.g., network issues, API not available),
      // treat as unauthenticated but don't block the UI.
      // Workspace-authorization denial routes through AccessDeniedScreen
      // rather than "Not signed in"; see AuthState.accessDenied for the dual
      // 401/403 contract this branch covers.
      setCurrentUserEmail(null);
      setCurrentUserPermissions(null);
      const accessDenied = isWorkspaceAccessDenied(err);
      setState({
        isLoading: false,
        isAuthenticated: false,
        user: null,
        error: err instanceof Error ? err.message : 'Failed to fetch user',
        accessDenied,
      });
    }
  };

  useEffect(() => {
    fetchUser();
  }, []);

  const value: AuthContextValue = {
    ...state,
    refetch: fetchUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * Hook to get user info, returns null if not authenticated
 */
export function useUser(): AuthUser | null {
  const { user } = useAuth();
  return user;
}

/**
 * Hook to check if user is authenticated
 */
export function useIsAuthenticated(): boolean {
  const { isAuthenticated } = useAuth();
  return isAuthenticated;
}
